import { useParams } from "react-router";
import { useEffect, useState, useMemo } from "react";
import { MODELS, useMultiModel } from "../../../api";
import { useSparkContext } from "../context/SparkContext.jsx";
import { injectGlossaryMarks } from "../../../design-system/components/BlockContent/glossary";
import { GlossaryDescriptionsCtx } from "../../../design-system/components/BlockContent/components/GlossaryEntry/GlossaryDescriptionsCtx";
import { SparkStepOverviewWithData } from "./SparkStepOverviewWithData.jsx";
import Overlay, { constructIdentifier } from "./components/Overlay/Overlay.jsx";

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000; // 86400000

/**
 * SparkStep component is responsible for rendering the current step of a spark within a workshop.
 * It utilizes URL parameters to determine the current workshop, spark, and step, then fetches the relevant data to display.
 *
 * @component
 * @returns {JSX.Element} The rendered component, which includes the current step step2, navigation between steps, and additional context.
 */
const SparkStepOverview = () => {
  const { stepSlug } = useParams();
  const { workshop, spark, structure } = useSparkContext();
  const [me, currStep] = useMultiModel([
    MODELS.ME,
    MODELS.SPARK_SCHEDULE_STEP(spark.slug, stepSlug),
  ]);

  const stageSlug = currStep.stageSlug;

  const [allCurrentStageSteps, content, glossaryTerms] = useMultiModel([
    MODELS.SPARK_SCHEDULE_STEPS(spark.slug, stageSlug),
    currStep.content?.contentKey
      ? MODELS.SPARK_MODULE_CONTENT(currStep.content.contentKey)
      : null,
    spark.showGlossary ? MODELS.SPARK_GLOSSARY_TERMS(spark.slug) : null,
  ]);

  // Highlight glossary terms in the content up front; `foundTerms` are the
  // canonical terms actually marked, which we then load descriptions for.
  const { content: processedContent, terms: foundTerms } = useMemo(
    () =>
      glossaryTerms && Object.keys(glossaryTerms).length && content
        ? injectGlossaryMarks(content, glossaryTerms)
        : { content, terms: [] },
    [content, glossaryTerms],
  );

  // Non-suspending: the step renders immediately (highlights included) and the
  // tooltip descriptions pop in once this lands. Skipped when nothing matched.
  const [glossaryDescriptions] = useMultiModel(
    [
      foundTerms.length
        ? MODELS.SPARK_GLOSSARY_DESCRIPTIONS(spark.slug, foundTerms)
        : null,
    ],
    { lazy: true },
  );

  const glossaryDescriptionMap = useMemo(
    () =>
      glossaryDescriptions
        ? new Map(glossaryDescriptions.map((d) => [d.term, d]))
        : null,
    [glossaryDescriptions],
  );

  const currStage = useMemo(() => {
    if (!stageSlug || !structure) return null;
    return structure
      .flatMap((phase) => phase.stages)
      .find((stage) => stage.slug === stageSlug);
  }, [stageSlug, structure]);

  const stationSteps = useMemo(() => {
    if (!currStep.content?.station?._key || !(allCurrentStageSteps.length > 0))
      return null;
    return allCurrentStageSteps.filter(
      (step) => step.content?.station?._key === currStep.content?.station?._key,
    );
  }, [currStep, allCurrentStageSteps]);

  const { nextStep, prevStep } = useMemo(() => {
    if (!stepSlug || !(allCurrentStageSteps.length > 0))
      return { nextStep: null, prevStep: null };
    const currStepIdx = allCurrentStageSteps.findIndex(
      (step) => step.slug === stepSlug,
    );
    return {
      nextStep: allCurrentStageSteps[currStepIdx + 1],
      prevStep: currStepIdx > 0 && allCurrentStageSteps[currStepIdx - 1],
    };
  }, [stepSlug, allCurrentStageSteps]);

  const [openOverlay, setOpenOverlay] = useState(false);
  // Only show overlay if workshop is not completed, the sparkStateQuery is set to true and user is a teacher
  const preventOverlay = Boolean(
    !workshop.id ||
    !currStep.sparkStateQuery ||
    workshop.state === "completed" ||
    !me.email ||
    me.isAdmin,
  );
  useEffect(() => {
    if (preventOverlay) return;

    const now = Date.now();
    const identifier = constructIdentifier(workshop.id);
    const dateOfLastVisit = localStorage.getItem(identifier);

    // if no date of last visit, show overlay
    if (!dateOfLastVisit) {
      setOpenOverlay(true);
      return;
    }

    const lastVisitTimestamp = parseInt(dateOfLastVisit, 10);

    // if last visit was more than 24 hours ago, show overlay
    if (now - lastVisitTimestamp >= ONE_DAY_IN_MS) {
      setOpenOverlay(true);
    }
  }, [preventOverlay, workshop.id]);

  return (
    <GlossaryDescriptionsCtx.Provider value={glossaryDescriptionMap}>
      {openOverlay && (
        <Overlay
          workshopId={workshop.id}
          onClose={() => setOpenOverlay(false)}
        />
      )}
      <SparkStepOverviewWithData
        step={currStep}
        content={processedContent}
        stationSteps={stationSteps}
        nextStep={nextStep}
        prevStep={prevStep}
        stageName={currStage?.name}
      />
    </GlossaryDescriptionsCtx.Provider>
  );
};

export default SparkStepOverview;
